import type {
    ExtensionSettingsFieldKey,
    OverviewWidgetId,
    OverviewWidgetItem,
} from "app/pages/sales-intelligence/modules/extension-management/types";
import type { track as TrackFn } from "pro-features/components/WithTrack/src/context";

const SETTINGS_TRACKING_KEYS: Record<ExtensionSettingsFieldKey, string> = {
    showFloatingBadge: "floating icon",
    enableGmailAgent: "gmail agent",
    enableGoogleCalendarAgent: "google calendar agent",
    enableLinkedin: "linkedin",
    autoOpenLinkedin: "auto open",
    enableSalesforce: "salesforce",
    autoOpenSalesforceEnabled: "salesforce auto open",
    enableHubspot: "hubspot",
    autoOpenHubspotEnabled: "hubspot auto open",
    enableDynamics: "dynamics",
    autoOpenDynamicsEnabled: "dynamics auto open",
    enableSalesloft: "salesloft",
    autoOpenSalesloftEnabled: "salesloft auto open",
    enableOutlookCalendar: "outlook calendar",
    enableGoogleWorkspace: "google workspace",
    enableGmail: "gmail assistant",
    enableGoogleCalendar: "google calendar meeting",
    enableGoogleSheets: "google sheets",
};

const WIDGET_TRACKING_NAME_MAP: Record<OverviewWidgetId, string> = {
    firmographics: "Company Data",
    signals: "Signals feed",
    salesforceActivity: "Salesforce Activity",
    trafficVisits: "Traffic Over Time",
    engagement: "Avg. Monthly Engagement",
    trafficShare: "Traffic Share",
    ecommerce: "Ecommerce Overview",
    adNetworks: "Ad Network",
    technologies: "Technologies",
};

const TAB_NAMES = ["insights and adoption", "configuration", "deployment"] as const;

const createExtensionManagementTrackingService = (track: TrackFn) => {
    const trackAction = (action: string, eventName: string) => {
        track("extension management", action, eventName);
    };

    return {
        trackTabSwitch: (tabIndex: number) => {
            const tabName = TAB_NAMES[tabIndex] ?? String(tabIndex);
            trackAction("click tab", `tab/${tabName}`);
        },

        trackBreadcrumbClick: (state: string) => {
            trackAction("click breadcrumb", `breadcrumb/${state}`);
        },

        trackChromeWebStoreLink: () => {
            trackAction("click chrome web store link", "chrome web store link");
        },

        trackSettingsToggle: (key: ExtensionSettingsFieldKey, currentValue: boolean) => {
            const trackingName = SETTINGS_TRACKING_KEYS[key];
            const willEnable = !currentValue;
            trackAction(
                willEnable ? `enable ${trackingName}` : `disable ${trackingName}`,
                `extension settings/${trackingName}/${willEnable ? "enable" : "disable"}`,
            );
        },

        trackAddWidget: (widgetId: OverviewWidgetId, widgets: OverviewWidgetItem[]) => {
            const selectedCount = widgets.filter((w) => w.isChecked || w.id === widgetId).length;
            trackAction("add widget", `${WIDGET_TRACKING_NAME_MAP[widgetId]}/${selectedCount}`);
        },

        trackRemoveWidget: (widgetId: OverviewWidgetId, widgets: OverviewWidgetItem[]) => {
            const selectedCount = widgets.filter((w) => w.isChecked && w.id !== widgetId).length;
            trackAction("remove widget", `${WIDGET_TRACKING_NAME_MAP[widgetId]}/${selectedCount}`);
        },

        trackReorderWidgets: () => {
            trackAction("reorder overview widgets", "configuration/overview widgets/reorder");
        },

        trackBannerLearnMore: () => {
            trackAction("click banner learn more", "configuration/banner learn more");
        },

        trackSave: () => {
            trackAction("click save", "configuration/save");
        },

        trackCancel: () => {
            trackAction("click cancel", "configuration/cancel");
        },

        trackStatusFilterChange: (filterValue: string) => {
            trackAction("filter status tab", `insights/status filter/${filterValue}`);
        },

        trackSearchUsers: () => {
            trackAction("search users", "insights/search users");
        },

        trackExportExcel: () => {
            trackAction("click export excel", "insights/export excel");
        },

        trackSendSetupGuide: () => {
            trackAction("click send setup guide", "insights/send setup guide");
        },

        trackCopyEmail: () => {
            trackAction("click copy email", "insights/copy email");
        },

        trackDeploymentSendGuide: () => {
            trackAction("click send guide card", "deployment/send guide");
        },

        trackDeploymentViewItInstructions: () => {
            trackAction("click it instructions card", "deployment/view it instructions");
        },

        trackItInstructionsCopy: () => {
            trackAction("click it instructions copy", "deployment/it instructions/copy");
        },

        trackItInstructionsGoogleAdminLink: () => {
            trackAction("click it instructions google admin link", "deployment/it instructions/google admin link");
        },

        trackItInstructionsIntuneLink: () => {
            trackAction("click it instructions intune link", "deployment/it instructions/intune link");
        },

        trackItInstructionsGotIt: () => {
            trackAction("click it instructions got it", "deployment/it instructions/got it");
        },
    };
};

export default createExtensionManagementTrackingService;
